import type { AppDispatch, RootState } from '../store';
import { deselectEntriesThatWillGoAwayFromComponentDesignUndoRedo } from '../store/actions/editorActions';
import { withoutCreatingUndoSnapshot } from '../store/middlewares/editorMiddleware';
import { setUnsavedComponentDesignFromServer } from '../store/slices/componentDesignsSlice';
import { componentDesignUndoRedoChainReplaced } from '../store/slices/editorSlice';
import { layoutSlice } from '../store/slices/layoutSlice';

/**
 * The trusses and their undo histories, and the layout's, as they stood before a tool ran.
 */
export interface EditCheckpoint {
  readonly componentDesigns: RootState['componentDesigns']['data'];
  readonly undoRedoChains: RootState['editor']['undoRedoChains'];
  readonly layoutUndo: RootState['layout']['undoRedoChain']['undo'];
}

export function takeEditCheckpoint(state: RootState): EditCheckpoint {
  return {
    componentDesigns: state.componentDesigns.data,
    undoRedoChains: state.editor.undoRedoChains,
    layoutUndo: state.layout.undoRedoChain.undo,
  };
}

/**
 * The trusses the designer could undo a change to since the checkpoint, with how each stood then. A truss whose undo
 * history didn't move was only re-saved, which the server's reply to a save does without making an undo step.
 */
function getEditedTrusses(checkpoint: EditCheckpoint, state: RootState) {
  return Object.entries(checkpoint.componentDesigns).filter(
    ([guid, before]) =>
      state.componentDesigns.data[guid] != null &&
      state.componentDesigns.data[guid] !== before &&
      state.editor.undoRedoChains[guid] !== checkpoint.undoRedoChains[guid],
  );
}

/**
 * Makes everything since the checkpoint undo with one Ctrl+Z, on each truss and on the layout, so the designer can take
 * back a tool's edit whole, however many steps the app took to make it.
 */
export function mergeUndoStepsSince(checkpoint: EditCheckpoint, dispatch: AppDispatch, getState: () => RootState) {
  for (const [guid, before] of getEditedTrusses(checkpoint, getState())) {
    const previous = checkpoint.undoRedoChains[guid]?.undo ?? null;
    dispatch(
      componentDesignUndoRedoChainReplaced({
        componentDesignGuid: guid,
        // The history never holds the truss as it stands, so a step that would only go back to it isn't added.
        chain: { undo: previous?.state === before ? previous : { state: before, previous }, redo: null },
      }),
    );
  }

  dispatch(layoutSlice.actions.undoStepsMerged({ since: checkpoint.layoutUndo }));
}

/**
 * Puts each truss changed since the checkpoint back as it was, undo history and all, so a tool that fails leaves no
 * truss half changed. Returns the names of the trusses put back. The layout is left as it is; its changes undo as one
 * step.
 */
export function revertTrussesTo(checkpoint: EditCheckpoint, dispatch: AppDispatch, getState: () => RootState) {
  const reverted: string[] = [];
  for (const [guid, before] of getEditedTrusses(checkpoint, getState())) {
    dispatch(deselectEntriesThatWillGoAwayFromComponentDesignUndoRedo(before));
    dispatch(
      setUnsavedComponentDesignFromServer(
        withoutCreatingUndoSnapshot({ componentDesignGuid: guid, componentDesign: before }),
      ),
    );
    dispatch(
      componentDesignUndoRedoChainReplaced({ componentDesignGuid: guid, chain: checkpoint.undoRedoChains[guid] }),
    );
    reverted.push(before.component.name);
  }

  dispatch(layoutSlice.actions.undoStepsMerged({ since: checkpoint.layoutUndo }));
  return reverted;
}
